import { isAxiosError } from "axios";
import { sanitizeClientRequestId } from "./client-log-policy";

export type ClientErrorKind =
  | "OFFLINE"
  | "NETWORK"
  | "TIMEOUT"
  | "CANCELED"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "VALIDATION"
  | "RATE_LIMITED"
  | "SERVER"
  | "UNKNOWN";

export interface ClientErrorPresentation {
  kind: ClientErrorKind;
  title: string;
  message: string;
  errorCode: string;
  requestId?: string;
  retryable: boolean;
  status?: number;
}

interface ErrorResponseData {
  message?: unknown;
  error?: unknown;
  code?: unknown;
}

interface OnlineTarget {
  navigator?: { onLine?: boolean };
}

export class ClientOfflineError extends Error {
  readonly code = "CLIENT_OFFLINE";

  constructor() {
    super("인터넷 연결을 확인한 뒤 다시 시도해 주세요.");
    this.name = "ClientOfflineError";
  }
}

export function isClientOnline(target: OnlineTarget = globalThis as OnlineTarget) {
  return target.navigator?.onLine !== false;
}

function plainText(value: unknown) {
  const joined = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string").join(", ")
    : typeof value === "string"
      ? value
      : undefined;
  if (!joined) return undefined;

  const normalized = joined
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalized ? normalized.slice(0, 300) : undefined;
}

function responseRequestId(error: unknown) {
  if (!isAxiosError(error)) return undefined;
  const headerValue = error.response?.headers?.["x-request-id"];
  const candidate =
    typeof headerValue === "string"
      ? headerValue
      : typeof error.config?.headers?.get === "function"
        ? error.config.headers.get("X-Request-ID")
        : undefined;
  const sanitized = sanitizeClientRequestId(typeof candidate === "string" ? candidate : undefined);
  return sanitized === "없음" ? undefined : sanitized;
}

function errorCode(error: unknown, status?: number, responseCode?: unknown) {
  if (typeof responseCode === "string" && responseCode.trim()) return responseCode.slice(0, 50);
  if (status) return `HTTP_${status}`;
  if (isAxiosError(error) && error.code) return error.code.slice(0, 50);
  if (error instanceof ClientOfflineError) return error.code;
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = String(error.code).trim();
    if (code) return code.slice(0, 50);
  }
  if (error instanceof Error) return error.name.slice(0, 50);
  return "UNKNOWN_ERROR";
}

export function mapClientError(
  error: unknown,
  options: { fallback?: string; online?: boolean } = {},
): ClientErrorPresentation {
  const fallback = options.fallback || "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  const online = options.online ?? isClientOnline();
  const requestId = responseRequestId(error);

  if (isAxiosError(error) && error.code === "ERR_CANCELED") {
    return {
      kind: "CANCELED",
      title: "요청 취소",
      message: "요청이 취소되었습니다.",
      errorCode: error.code,
      requestId,
      retryable: false,
    };
  }
  if (isAxiosError(error) && (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT")) {
    return {
      kind: "TIMEOUT",
      title: "응답 시간 초과",
      message: "응답이 지연되고 있습니다. 처리 결과를 확인한 뒤 다시 시도해 주세요.",
      errorCode: error.code,
      requestId,
      retryable: false,
    };
  }

  if (!online || error instanceof ClientOfflineError) {
    return {
      kind: "OFFLINE",
      title: "인터넷 연결 없음",
      message: "인터넷 연결을 확인한 뒤 다시 시도해 주세요.",
      errorCode: "CLIENT_OFFLINE",
      requestId,
      retryable: false,
    };
  }

  const providerCode = errorCode(error);
  if (providerCode === "auth/network-request-failed") {
    return {
      kind: "NETWORK",
      title: "인증 서버 연결 실패",
      message: "인증 서버와 통신할 수 없습니다. 네트워크 상태를 확인해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: true,
    };
  }
  if (providerCode === "auth/too-many-requests") {
    return {
      kind: "RATE_LIMITED",
      title: "잠시 후 다시 시도",
      message: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: true,
    };
  }
  if (
    [
      "auth/invalid-credential",
      "auth/invalid-login-credentials",
      "auth/user-not-found",
      "auth/wrong-password",
    ].includes(providerCode)
  ) {
    return {
      kind: "VALIDATION",
      title: "로그인 정보 확인",
      message: "이메일 또는 비밀번호를 확인해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }
  if (providerCode === "auth/invalid-email" || providerCode === "auth/weak-password") {
    return {
      kind: "VALIDATION",
      title: "입력 내용 확인",
      message:
        providerCode === "auth/invalid-email"
          ? "올바른 이메일 주소를 입력해 주세요."
          : "더 안전한 비밀번호를 입력해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }
  if (providerCode === "auth/email-already-in-use") {
    return {
      kind: "CONFLICT",
      title: "이미 가입된 계정",
      message: "이미 가입된 이메일입니다. 로그인하거나 비밀번호 찾기를 이용해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }
  if (providerCode === "auth/popup-closed-by-user") {
    return {
      kind: "UNKNOWN",
      title: "로그인 취소",
      message: "로그인이 취소되었습니다.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }
  if (providerCode === "auth/requires-recent-login") {
    return {
      kind: "UNAUTHORIZED",
      title: "재인증 필요",
      message: "보안을 위해 다시 로그인한 뒤 진행해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }
  if (providerCode.startsWith("auth/")) {
    return {
      kind: "UNKNOWN",
      title: "인증 요청 실패",
      message: "인증 요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.",
      errorCode: providerCode,
      requestId,
      retryable: false,
    };
  }

  if (isAxiosError<ErrorResponseData>(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data;
    const serverMessage = plainText(responseData?.message) || plainText(responseData?.error);
    const code = errorCode(error, status, responseData?.code);

    if (!status) {
      return {
        kind: "NETWORK",
        title: "서버 연결 실패",
        message: "서버와 통신할 수 없습니다. 네트워크 상태를 확인해 주세요.",
        errorCode: code,
        requestId,
        retryable: true,
      };
    }

    if (status === 401) {
      return {
        kind: "UNAUTHORIZED",
        title: "로그인 확인 필요",
        message: serverMessage || "로그인 정보가 만료되었습니다. 다시 로그인해 주세요.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status === 403) {
      return {
        kind: "FORBIDDEN",
        title: "권한 없음",
        message: serverMessage || "이 작업을 수행할 권한이 없습니다.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status === 404 || status === 410) {
      return {
        kind: "NOT_FOUND",
        title: "정보를 찾을 수 없음",
        message: serverMessage || "요청한 정보를 찾을 수 없습니다.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status === 409) {
      return {
        kind: "CONFLICT",
        title: "현재 상태 확인 필요",
        message: serverMessage || "이미 처리되었거나 현재 상태에서는 진행할 수 없습니다.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status === 400 || status === 422) {
      return {
        kind: "VALIDATION",
        title: "입력 내용 확인",
        message: serverMessage || "입력 내용을 확인해 주세요.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status === 429) {
      return {
        kind: "RATE_LIMITED",
        title: "잠시 후 다시 시도",
        message: serverMessage || "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
        errorCode: code,
        requestId,
        retryable: true,
        status,
      };
    }
    if (status === 428) {
      return {
        kind: "VALIDATION",
        title: "추가 확인 필요",
        message: serverMessage || "요청을 계속하려면 추가 확인이 필요합니다.",
        errorCode: code,
        requestId,
        retryable: false,
        status,
      };
    }
    if (status >= 500) {
      return {
        kind: "SERVER",
        title: "서비스 일시 오류",
        message: "서비스 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.",
        errorCode: code,
        requestId,
        retryable: true,
        status,
      };
    }
  }

  const message = error instanceof Error ? plainText(error.message) || fallback : fallback;
  return {
    kind: "UNKNOWN",
    title: "요청 처리 실패",
    message,
    errorCode: errorCode(error),
    requestId,
    retryable: false,
  };
}

export function shouldRetryClientQuery(failureCount: number, error: unknown) {
  if (failureCount >= 2) return false;
  return mapClientError(error).retryable;
}
